import { ref, onMounted, watch } from "vue";
import { usePage } from "@inertiajs/vue3";
import axios from "axios";

export function useSyncLocaleAndContext() {
    const page = usePage();

    const write = () => {
        // 1) locale from page.props
        const locale =
            page.props?.locale ||
            page.props?.ziggy?.locale ||
            page.props?.app?.locale;
        if (locale) localStorage.setItem("locale", String(locale));

        // 2) team id if you want team-scoped permissions server-side
        const teamId =
            page.props?.auth?.user?.current_team_id ||
            page.props?.auth?.user?.currentTeam?.id;
        if (teamId) localStorage.setItem("team_id", String(teamId));

        // 3) UI context: 'nova' for backoffice, 'app' for jetstream
        const ctx =
            typeof window !== "undefined" &&
            location.pathname.startsWith("/admin")
                ? "nova"
                : "app";
        localStorage.setItem("ui_context", ctx);
    };

    onMounted(write);
    // Re-run when Inertia updates props (locale switch, team switch, etc.)
    watch(() => page.props, write, { deep: true });
}

export const omit = (targetObj, props) => {
    const obj = Object.assign({}, targetObj);
    if (!Array.isArray(props)) {
        return;
    }

    props.forEach((prop) => {
        if (obj.hasOwnProperty(prop)) {
            delete obj[prop];
        }
    });

    return obj;
};

export const initialCasing = (str) => {
    if (str) {
        return str.replace(/(?:^|_)(\w)/g, function (_, c) {
            return c.toUpperCase();
        });
    }
    return "";
};

export function customNovaAxios(currentLang) {
    const instance = axios.create();

    instance.interceptors.request.use((config) => {
        config.headers = config.headers || {};
        config.headers["X-Context"] = "nova";

        if (currentLang) {
            config.headers["X-Locale"] = currentLang;
        }

        console.log("Intercepted Headers:", config.headers);
        return config;
    });

    return instance;
}

export const debounce = (func, delay) => {
    let timerId;
    return function (...args) {
        clearTimeout(timerId);
        timerId = setTimeout(() => {
            func.apply(this, args);
        }, delay);
    };
};

export const delay = (delay) => {
    return new Promise((resolve) => {
        setTimeout(() => {
            resolve();
        }, delay);
    });
};

export const DEFAULT_PAGE_NUMBER = 1;
export const DEFAULT_PAGE_SIZE = 32;

export const updateMultiValue = (event, property, form) => {
    form[property] = event.id;
};

export const isTrippleBoolean = (value) => {
    return value === true || value === false || value === null;
};

export const getKeyValueFromId = (id, options) => {
    const option = options.find((option) => option.id === id);

    return option ? option.id : null;
};

export const getKeyFromValue = (value, options, isString) => {
    if (isString !== undefined && value == null) {
        value = "";
    }
    return options.find((option) => option.id === value);
};

export const getObjectFromId = (ids, options) => {
    if (!ids?.length || !options?.value?.length) {
        return [];
    }

    if (!Array.isArray(ids)) {
        ids = [ids];
    }

    const lowerCaseIds = ids.map((id) =>
        id === null ? null : id.toLowerCase(),
    );

    return options.value.filter((option) =>
        lowerCaseIds.includes(option.id.toLowerCase()),
    );
};

export const getGroupObjectFromId = (ids, options) => {
    if (!ids?.length || !options?.value?.length) {
        return [];
    }

    if (!Array.isArray(ids)) {
        ids = [ids];
    }

    const lowerCaseIds = ids.map((id) => id.toLowerCase());

    const groupIds = options.value.flatMap((option) => {
        const values = Object.values(option.name.__t).slice(1);
        return Object.keys(option.name.__t)
            .slice(1)
            .map((key, index) => ({
                id: option.id + "_" + key,
                name: values[index],
            }));
    });

    return groupIds.filter((groupId) =>
        lowerCaseIds.includes(groupId.id.toLowerCase()),
    );
};

// export const mappedContentItemTypes = contentItemTypes.map((type) => ({ ...type, value: initialCasing(type.value) }));

const useCountdown = (initialMinutes, up) => {
    const minutes = ref(initialMinutes);
    const seconds = ref(minutes.value * 60);
    const minutesDisplay = ref(["0", "0"]);
    const secondsDisplay = ref(["0", "0"]);
    let intervalId;

    const updateCountdown = () => {
        minutesDisplay.value = String(Math.floor(seconds.value / 60))
            .padStart(2, "0")
            .split("");
        secondsDisplay.value = String(seconds.value % 60)
            .padStart(2, "0")
            .split("");

        if (seconds.value < 1 && !up) {
            clearInterval(intervalId);
        }
    };

    const startCountdown = () => {
        // Clear any existing intervals to avoid duplicates
        if (intervalId) {
            clearInterval(intervalId);
        }

        // Start the countdown interval
        intervalId = setInterval(() => {
            if (up) seconds.value++;
            else seconds.value--;
            updateCountdown();
        }, 1000);
    };

    const stopCountdown = () => {
        if (intervalId) {
            clearInterval(intervalId);
            intervalId = null;
            seconds.value = 0;
        }
    };

    onMounted(() => {
        updateCountdown();
    });

    return {
        minutesDisplay,
        secondsDisplay,
        remainingTime: seconds,
        startCountdown,
        stopCountdown,
    };
};

export default useCountdown;

export const interventions = [
    {
        key: "written_word",
        name: "intervention.Geschreven woord",
        image: "/images/svg/Geschreven woord.svg",
        image_dark: "/images/svg/Geschreven woord_dark.svg",
    },
    {
        key: "image",
        name: "intervention.Afbeelding",
        image: "/images/svg/Afbeelding.svg",
        image_dark: "/images/svg/Afbeelding_dark.svg",
    },
    {
        key: "image_association",
        name: "intervention.Associatie",
        image: "/images/svg/Associatie.svg",
        image_dark: "/images/svg/Associatie_dark.svg",
    },
    {
        key: "begin_sound",
        name: "intervention.Begin klank",
        image: "/images/svg/Geschreven woord.svg",
        image_dark: "/images/svg/Geschreven woord_dark.svg",
    },
    {
        key: "begin_sound_association_images",
        name: "intervention.Associatie",
        image: "/images/svg/Associatie.svg",
        image_dark: "/images/svg/Associatie_dark.svg",
    },
    {
        key: "begin_sound_keyword_images",
        name: "intervention.Sleutelwoord",
        image: "/images/svg/Sleutelwoord.svg",
        image_dark: "/images/svg/Sleutelwoord_dark.svg",
    },
    {
        key: "mouth_video",
        name: "intervention.Begin klank",
        image: "/images/svg/Gesproken video.svg",
        image_dark: "/images/svg/Gesproken video_dark.svg",
    },
    {
        key: "spoken_audio",
        name: "intervention.Gesproken woord",
        image: "/images/svg/Gesproken woord.svg",
        image_dark: "/images/svg/Gesproken woord_dark.svg",
    },

    {
        key: "mouth_image",
        name: "intervention.Begin klank",
        image: "/images/svg/Afbeelding.svg",
        image_dark: "/images/svg/Associatie_dark.svg",
    },

    {
        key: "rhyme_word",
        name: "intervention.Rijmwoord",
        image: "/images/svg/Rijmwoord.svg",
        image_dark: "/images/svg/Rijmwoord_dark.svg",
    },

    {
        key: "completion_sentence",
        name: "intervention.Aanvulzin",
        image: "/images/svg/Aanvulzin.svg",
        image_dark: "/images/svg/Aanvulzin_dark.svg",
    },
    {
        key: "spoken_video",
        name: "intervention.Video gesproken",
        image: "/images/svg/Gesproken video.svg",
        image_dark: "/images/svg/Gesproken video_dark.svg",
    },
    {
        key: "begin_letter",
        name: "intervention.Begin letter",
        image: "/images/svg/Eerste letter.svg",
        image_dark: "/images/svg/Eerste letter_dark.svg",
    },

    {
        key: "theme",
        name: "intervention.Categorie",
        image: "/images/svg/Associatie.svg",
        image_dark: "/images/svg/Eerste letter_dark.svg",
    },
    {
        key: "synonym",
        name: "intervention.Synoniem",
        image: "/images/svg/Synoniem.svg",
        image_dark: "/images/svg/Synoniem_dark.svg",
    },

    {
        key: "description",
        name: "intervention.Omschrijving",
        image: "/images/svg/Omschrijving.svg",
        image_dark: "/images/svg/Omschrijving_dark.svg",
    },
    {
        key: "begin_sound_audio",
        name: "intervention.Begin klank",
        image: "/images/svg/Gesproken woord.svg",
        image_dark: "/images/svg/Gesproken woord_dark.svg",
    },
    {
        key: "strategies",
        name: "intervention.Strategieën",
        image: "/images/svg/Strategieen.svg",
        image_dark: "/images/svg/Strategieen_dark.svg",
    },
    {
        key: "description_audio",
        name: "intervention.Omschrijving",
        image: "/images/svg/Omschrijving.svg",
        image_dark: "/images/svg/Omschrijving_dark.svg",
    },
    {
        key: "completion_sentence_audio",
        name: "intervention.Aanvulzin",
        image: "/images/svg/Aanvulzin.svg",
        image_dark: "/images/svg/Aanvulzin_dark.svg",
    },
    {
        key: "begin_sound_image",
        name: "intervention.Begin klank",
        image: "/images/svg/Gesproken woord.svg",
        image_dark: "/images/svg/Gesproken woord_dark.svg",
    },
    {
        key: "letter_box",
        name: "intervention.input_letter_box",
        image: "/images/png/boxed-typing.png",
        image_dark: "/images/png/boxed-typing.png",
    },
    {
        key: "next_letter",
        name: "intervention.input_next_letter",
        image: "/images/png/next-letter.png",
        image_dark: "/images/png/next-letter.png",
    },
    {
        key: "video",
        name: "intervention.Video",
        image: "/images/svg/Gesproken video.svg",
        image_dark: "/images/svg/Gesproken video_dark.svg",
    },
    {
        key: "begin_sound_mouth_video",
        name: "intervention.Begin Sound Mouth Video",
        image: "/images/svg/Gesproken video.svg",
        image_dark: "/images/svg/Gesproken video_dark.svg",
    },
    {
        key: "direct_feedback",
        name: "intervention.Direct Feedback",
        image: "",
        image_dark: "",
    },
    {
        key: "begin_sound_mouth_image",
        name: "intervention.Begin Sound Mouth Image",
        image: "/images/svg/Gesproken video.svg",
        image_dark: "/images/svg/Gesproken video_dark.svg",
    },
];

export const settingInterventions = [
    { key: "direct_feedback", name: "intervention.Direct Feedback" },
    { key: "written_word", name: "intervention.Written Word" },
    { key: "image", name: "intervention.Image" },
    { key: "image_association", name: "intervention.Image Association" },
    { key: "begin_sound", name: "intervention.Begin Sound" },
    {
        key: "begin_sound_association_images",
        name: "intervention.Begin Sound Association Images",
    },
    {
        key: "begin_sound_keyword_images",
        name: "intervention.Begin Sound Keyword Images",
    },
    { key: "mouth_video", name: "intervention.Mouth Video" },
    { key: "spoken_audio", name: "intervention.Spoken Audio" },
    { key: "mouth_image", name: "intervention.Mouth Image" },
    { key: "rhyme_word", name: "intervention.Rhyme Word" },
    { key: "completion_sentence", name: "intervention.Completion Sentence" },
    { key: "spoken_video", name: "intervention.Spoken Video" },
    { key: "begin_letter", name: "intervention.Begin Letter" },
    { key: "theme", name: "intervention.Theme" },
    { key: "synonym", name: "intervention.Synonym" },
    { key: "description", name: "intervention.Description" },
    { key: "begin_sound_audio", name: "intervention.Begin Sound Audio" },
    { key: "strategies", name: "intervention.Strategies" },
    { key: "description_audio", name: "intervention.Description Audio" },
    {
        key: "completion_sentence_audio",
        name: "intervention.Completion Sentence Audio",
    },
    { key: "begin_sound_image", name: "intervention.Begin Sound Image" },
    { key: "repetition", name: "intervention.Repetition" },
    { key: "metronome", name: "intervention.Metronome" },
    { key: "video", name: "intervention.Video" },
    {
        key: "begin_sound_mouth_video",
        name: "intervention.Begin Sound Mouth Video",
    },
    {
        key: "begin_sound_mouth_image",
        name: "intervention.Begin Sound Mouth Image",
    },
];

export const defaultInterventionSettings = Object.fromEntries(
    settingInterventions.map(({ key }) => [key, true]),
);

export const normalizeInterventionSettings = (interventions) => {
    if (!interventions) {
        return { ...defaultInterventionSettings };
    }

    let parsedInterventions;

    try {
        parsedInterventions =
            typeof interventions === "string"
                ? JSON.parse(interventions)
                : interventions;
    } catch {
        return { ...defaultInterventionSettings };
    }

    if (!parsedInterventions || typeof parsedInterventions !== "object") {
        return { ...defaultInterventionSettings };
    }

    return {
        ...defaultInterventionSettings,
        ...parsedInterventions,
    };
};

export const generateUUID = () => {
    let uuid = "";
    const characters = "abcdef0123456789";
    for (let i = 0; i < 36; i++) {
        if (i === 8 || i === 13 || i === 18 || i === 23) {
            uuid += "-";
        } else if (i === 14) {
            uuid += "4";
        } else if (i === 19) {
            uuid += characters.charAt(
                Math.floor(Math.random() * characters.length),
            );
        } else {
            uuid += characters.charAt(
                Math.floor(Math.random() * characters.length),
            );
        }
    }
    return uuid;
};

export const formatDateString = (date) => {
    var year = date.getFullYear();
    var month = padZero(date.getMonth() + 1);
    var day = padZero(date.getDate());
    var hours = padZero(date.getHours());
    var minutes = padZero(date.getMinutes());
    var seconds = padZero(date.getSeconds());

    return (
        year +
        "-" +
        month +
        "-" +
        day +
        " " +
        hours +
        ":" +
        minutes +
        ":" +
        seconds
    );
};

function padZero(number) {
    return (number < 10 ? "0" : "") + number;
}

const EMPTY_FORMATTED_DATE = ["--", "--:--"];

export function formatDateWithTimezone(rawDateString, options = {}) {
    const trimmed = rawDateString?.trim();
    if (!trimmed) return EMPTY_FORMATTED_DATE;

    const { timeZone = "Europe/Amsterdam", isLocal = false } = options;

    if (isLocal) {
        return formatLocalDateParts(trimmed);
    }

    const hasIsoDelimiter = trimmed.includes("T");
    const normalized = hasIsoDelimiter ? trimmed : trimmed.replace(" ", "T");
    const hasExplicitOffset = /(Z|[+-]\d{2}:?\d{2})$/.test(trimmed);
    const isoCandidate = hasExplicitOffset ? normalized : `${normalized}Z`;
    const date = new Date(isoCandidate);

    if (Number.isNaN(date.getTime())) {
        return EMPTY_FORMATTED_DATE;
    }

    return formatPartsForTimezone(date, timeZone);
}

function formatLocalDateParts(dateTimeString) {
    const [datePart, timePart = "00:00"] = dateTimeString.split(/\s+/);
    if (!datePart) return EMPTY_FORMATTED_DATE;

    const [year, month, day] = datePart.split("-");
    if (!year || !month || !day) {
        return EMPTY_FORMATTED_DATE;
    }

    const [hour = "00", minute = "00"] = timePart.split(":");
    return [
        `${day}-${month}-${year}`,
        `${hour.padStart(2, "0")}:${minute.padStart(2, "0")}`,
    ];
}

function formatPartsForTimezone(date, timeZone) {
    const formatter = new Intl.DateTimeFormat("en-GB", {
        timeZone,
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
    });

    const partsMap = formatter.formatToParts(date).reduce((acc, part) => {
        acc[part.type] = part.value;
        return acc;
    }, {});

    return [
        `${partsMap.day}-${partsMap.month}-${partsMap.year}`,
        `${partsMap.hour}:${partsMap.minute}`,
    ];
}

export function formatStartDate(date, trimDate) {
    const dateObj = new Date(date);
    const day = dateObj.getDate().toString().padStart(2, "0");
    const month = (dateObj.getMonth() + 1).toString().padStart(2, "0");
    const year = dateObj.getFullYear();
    const hours = dateObj.getHours();
    const minutes = dateObj.getMinutes();
    // const ampm = hours >= 12 ? "PM" : "AM";
    // const hourFormat = hours % 12 || 12; // Convert midnight (0) to 12
    const formattedTime = `${hours}:${minutes.toString().padStart(2, "0")}`;

    return trimDate
        ? `${day}-${month}`
        : `${day}-${month}-${year} ${formattedTime}`;
}

export function convertTimeToMinutes(seconds) {
    let minutes = Math.floor(seconds / 60);
    let hours = Math.floor(minutes / 60);
    let remainingMinutes = minutes % 60;
    seconds = seconds % 60;

    let formattedTime = "";
    if (hours > 0) {
        formattedTime += hours.toString().padStart(2, "0") + ":";
    }
    formattedTime +=
        remainingMinutes.toString().padStart(2, "0") +
        ":" +
        seconds.toString().padStart(2, "0");

    return formattedTime;
}

export const formatDateObject = (dateObj) => {
    if (!dateObj) return null;
    let year = dateObj.getFullYear();
    let month = (1 + dateObj.getMonth()).toString().padStart(2, "0");
    let day = dateObj.getDate().toString().padStart(2, "0");

    return `${year}-${month}-${day}`;
};

export const formatText = (text) => {
    return text?.replace(/\n/g, "<br>");
};

export const mapContentType = (typeName) => {
    switch (typeName) {
        case "ContentItemWord":
            return "word";
        case "ContentItemText":
            return "text";
        case "ContentItemSentence":
            return "sentence";
        case "ContentItemLetter":
            return "letter";
        case "ContentItemSound":
            return "sound";
        case "ContentItemCategory":
            return "category";
        default:
            return null;
    }
};

export const extractValue = (obj) => {
    if (obj.content) {
        return obj.content;
    }
    if (obj.image) {
        return obj.image.url;
    }
    if (obj.audio) {
        return obj.audio.url;
    }
    return null; // In case none of the expected properties are found
};

//Expand function to support video types also later on.
export const checkContentType = (content) => {
    const imageExtensions = ["jpg", "jpeg", "png", "gif", "bmp", "svg"];
    const audioExtensions = ["mp3", "wav", "ogg", "m4a", "flac"];
    const extension = content.split(".").pop().toLowerCase();

    if (imageExtensions.includes(extension)) {
        return "image";
    } else if (audioExtensions.includes(extension)) {
        return "audio";
    } else {
        return "text";
    }
};

export const mapDistractorToRelation = (distractor) => {
    switch (distractor) {
        case "target_item":
            return "target_item";
        case "partDistractor":
            return "part";
        case "themeDistractor":
            return "theme";
        case "synonymDistractor":
            return "synonym";
        case "hyponymyDistractor":
            return "hyponymy";
        case "semanticDistractor":
            return "semantic_relation";
        case "homophoneDistractor":
            return "homophone";
        case "hypernymyDistractor":
            return "hypernymy";
        case "phonologicDistractor":
            return "phonological_relation";
        case "associationDistractor":
            return "associations";
        case "minimalPairDistractor":
            return "minimal_pair";
        case "rhymingWordDistractor":
            return "rhyming_word";
        case "actionDistractor":
            return "action_related";
        case "characteristicDistractor":
            return "characteristic_related";
        case "behaviourDistractor":
            return "behaviour_related";
        case "componentDistractor":
            return "component_related";
        case "abstractEntityDistractor":
            return "abstract_entity_related";
        case "participantDistractor":
            return "participant_related";
        case "locationDistractor":
            return "location_related";
        case "antonymnDistractor":
            return "antonym_related";
        case "emotionDistractor":
            return "emotion_related";
        case "partsUnrelated":
            return "parts_unrelated";
        case "themeUnrelated":
            return "theme";
        case "randomUnrelated":
            return "random_unrelated";
        case "synonymUnrelated":
            return "synonym_unrelated";
        case "semanticUnrelated":
            return "semantic_unrelated";
        case "rhymingWordUnrelated":
            return "rhyming_word_unrelated";
        case "actionUnrelated":
            return "action_unrelated";
        case "associationUnrelated":
            return "association_unrelated";
        case "phonologicUnrelated":
            return "phonologic_unrelated";
        default:
            return "";
    }
};

export const getAnswerObject = (answer, multiple) => {
    // Helper function to determine the type and content
    const createAnswerObject = (type, content, relation, audio_url = null) => ({
        type,
        content,
        relation,
        audio_url,
    });

    // Handle the case for multiple answers
    if (multiple) {
        const answerObjects = [];

        answer?.forEach((a) => {
            if (a?.image) {
                answerObjects.push(
                    createAnswerObject("image", a.image?.url, a.relation),
                );
            } else if (a?.audio) {
                answerObjects.push(
                    createAnswerObject("audio", a.audio?.url, a.relation),
                );
            } else if (a?.video) {
                answerObjects.push(
                    createAnswerObject("video", a.video?.url, a.relation),
                );
            } else if (a?.content_without_begin_letter) {
                answerObjects.push(
                    createAnswerObject(
                        "text",
                        "_" + a.content_without_begin_letter,
                    ),
                );
            } else if (a?.content) {
                answerObjects.push(
                    createAnswerObject("text", a.content, a.relation),
                );
            } else if (a?.text) {
                answerObjects.push(
                    createAnswerObject("text", a.text, a.relation),
                );
            } else if (a?.audio_and_content) {
                if (a?.audio_and_content?.audio) {
                    answerObjects.push(
                        createAnswerObject(
                            "audio",
                            a?.audio_and_content?.audio?.url,
                            a.relation,
                        ),
                    );
                }

                if (a?.audio_and_content?.content) {
                    answerObjects.push(
                        createAnswerObject(
                            "text",
                            a?.audio_and_content?.content,
                            a.relation,
                        ),
                    );
                }
            }
        });

        return answerObjects;
    }

    // Handle the case for a single answer
    if (answer?.image) {
        return createAnswerObject("image", answer.image?.url, answer?.relation);
    } else if (answer?.audio) {
        return createAnswerObject("audio", answer.audio?.url, answer?.relation);
    } else if (answer?.video) {
        return createAnswerObject("video", answer.video?.url, answer?.relation);
    } else if (answer?.content_without_begin_letter) {
        return createAnswerObject(
            "text",
            "_" + answer.content_without_begin_letter,
        );
    } else if (answer?.content) {
        return createAnswerObject("text", answer.content, answer?.relation);
    } else if (answer?.text) {
        return createAnswerObject("text", answer.text, answer?.relation);
    } else if (answer?.audio_and_content?.content) {
        return createAnswerObject(
            "audio_text",
            answer?.audio_and_content?.content,
            answer.relation,
            answer.audio_and_content?.audio?.url,
        );
    }

    // Return null or an empty object if no valid type is found
    return null;
};

import { createI18n } from "vue-i18n";

export function setupI18n(app) {
    // Eagerly include your frontend locale JSON files.
    // Adjust the glob to wherever your generator writes them.
    // Examples:
    //   /resources/lang/frontend/nl-nl.json
    //   /resources/lang/frontend/de-de.json
    const modules = import.meta.glob("/resources/lang/*.json", { eager: true });

    const messages = {};
    for (const path in modules) {
        const raw = modules[path].default || modules[path];
        const localeMatch = path.match(/\/([a-z-]+)\.json$/i);
        if (!localeMatch) continue;
        // const locale = localeMatch[1];
        // // Support both flat and nested JSON
        // messages[locale] = expandFlatKeys(raw);
        messages[localeMatch[1]] = raw;
    }

    const initialLocale =
        document.documentElement.getAttribute("lang") ||
        window.APP_LOCALE ||
        "nl-nl";

    const i18n = createI18n({
        legacy: false, // using Composition API
        globalInjection: true, // allows template usage of $t('key') without wiring t() in each component
        locale: initialLocale,
        fallbackLocale: "nl-nl",
        messages,
    });

    app.use(i18n);
    app.config.globalProperties.trans = app.config.globalProperties.$t;

    return i18n;
}

export async function getUserPrefs() {
    const { data } = await axios.get("/user/preferences");

    return data;
}

export async function setUserPref(key, value) {
    const { data } = await axios.post("/user/preferences", {
        key,
        value,
    });

    return data;
}
